import { useCallback, useEffect, useRef, useState } from 'react';
import type { Player } from '../types';
import { getStateName } from '../data/states';
import { CloseIcon } from './icons';

const WINNING_SCORE = 50;

interface Props {
  players: Player[];
  myPlayerId: string | null;
}

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

export function ScoreBoard({ players, myPlayerId }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const sorted = [...players].sort((a, b) => b.score - a.score);
  const myIndex = sorted.findIndex(p => p.id === myPlayerId);
  const me = myIndex >= 0 ? sorted[myIndex] : undefined;

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  return (
    <div className="w-full relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-controls="scoreboard-sheet"
        className="w-full flex items-center justify-between gap-2 rounded-xl px-4 py-3 bg-atlas-brass/10 dark:bg-atlas-brass-dark/15 text-sm text-atlas-ink dark:text-atlas-ink-dark"
      >
        <span className="truncate">
          {me ? (
            <>
              <span className="font-semibold">You: {me.score} pts</span>{' '}
              <span className="text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
                · {ordinal(myIndex + 1)} of {sorted.length}
              </span>
            </>
          ) : (
            <span className="font-semibold">
              Scoreboard · {sorted.length} player{sorted.length === 1 ? '' : 's'}
            </span>
          )}
        </span>
        <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-atlas-brass dark:text-atlas-brass-dark shrink-0">
          Standings
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={close} />
          <div
            id="scoreboard-sheet"
            className="absolute left-0 right-0 top-full z-30 mt-2 max-h-[60vh] flex flex-col rounded-2xl bg-atlas-surface dark:bg-atlas-surface-dark shadow-lg animate-fade-in"
          >
            <div className="flex items-center justify-between px-4 pt-3 pb-2 shrink-0">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
                Scoreboard
              </h2>
              <button
                type="button"
                onClick={close}
                aria-label="Close scoreboard"
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-atlas-ink-soft dark:text-atlas-ink-soft-dark -mr-2"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto px-4 pb-3 space-y-2">
              {sorted.map((player, rank) => {
                const isMe = player.id === myPlayerId;
                const progress = Math.min((player.score / WINNING_SCORE) * 100, 100);
                return (
                  <div
                    key={player.id}
                    className={`rounded-lg px-3 py-2 ${
                      isMe
                        ? 'bg-atlas-brass/10 dark:bg-atlas-brass-dark/15 border-l-4 border-atlas-brass dark:border-atlas-brass-dark'
                        : 'bg-atlas-bg dark:bg-atlas-bg-dark'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark w-4 shrink-0">
                          {rank + 1}.
                        </span>
                        <span className="font-semibold truncate text-sm text-atlas-ink dark:text-atlas-ink-dark">
                          {player.name}
                          {player.isComputer && (
                            <span className="ml-1 text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark">[CPU]</span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {player.currentState && (
                          <span
                            className={`text-xs font-bold w-6 text-center ${
                              player.isGPS ? 'text-atlas-pine dark:text-atlas-pine-dark' : 'text-atlas-brick dark:text-atlas-brick-dark'
                            }`}
                            title={`${getStateName(player.currentState)} — ${player.isGPS ? 'GPS located' : 'manually set'}`}
                          >
                            {player.currentState}
                          </span>
                        )}
                        <span className="font-mono font-bold text-base text-atlas-ink dark:text-atlas-ink-dark w-10 text-right tabular-nums">
                          {player.score}
                        </span>
                      </div>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-atlas-rule dark:bg-atlas-rule-dark overflow-hidden">
                      <div
                        className="h-full rounded-full bg-atlas-brass dark:bg-atlas-brass-dark transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark text-center px-4 py-3 shrink-0">
              First to {WINNING_SCORE} wins
            </p>
          </div>
        </>
      )}
    </div>
  );
}
